import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { makeStyles } from '@material-ui/core'
import { Button, Drawer, Form, Input, Select, Space, Upload } from 'antd'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { trackPromise, usePromiseTracker } from 'react-promise-tracker'
import { useData } from '../../../data'
import { CTAButton, FileLink } from '../components'

const useStyles = makeStyles(theme => ({
    error: {
        fontSize: 18,
        color: 'red'
    }
}))

const { Option } = Select

/**
 * Drawer to add a new job 
 */
const AddNewJob = ({ visible, close, tool_id }) => {
    const [toolList, setTools] = useState([])
    const [newToolList, setNewTools] = useState([])
    const [selectedTool, setTool] = useState(null)
    const [error, setError] = useState(null)
    const [toolid, setToolId] = useState(null);

    const [form] = Form.useForm()


    const data = useData()
    const jobs = data.jobs()
    const tools = data.tools()
    const classes = useStyles()



    // get a list of tools to use 
    const getTools = async () => {
        try {
            const allTools = await tools.getAllTools()

            setTools(allTools.map(t => ({ id: t.id, name: t.name, type: t.type, input_path: t.sample_input_file })))
            setNewTools(toolList)
        } catch (error) {
            console.log(error.message)
        }

    }


    /**
     * Get data required 
     */
    useEffect(() => {
        getTools()
        // eslint-disable-next-line
    }, [])

    // set tool id
    useEffect(() => {
        if (tool_id) {
            form.setFieldsValue({ tool: tool_id })
        }
    }, [tool_id, form])

    /**
     * Select a tool if tool id provided
     */
    useEffect(() => {
        if (tool_id) {
            if (toolList.length) {
                const tool = toolList.find(t => t.id === tool_id)

                if (tool) {
                    setTool(tool)
                }
            }
        }

        // eslint-disable-next-line
    }, [tool_id, toolList])



    const { promiseInProgress } = usePromiseTracker()

    const createJob = (vals) => {
        setError(null)

        if (tool_id) {
            form.setFieldsValue({ tool: tool_id })
        }

        // process the parametes 
        const { parameters = [] } = vals
        const params = {}

        for (let param of parameters) {
            const { param_key, param_value } = param

            params[param_key] = param_value
        }

        trackPromise(
            jobs.createJob({ ...vals, parameters: params })
                .then(job => {
                    close(job)
                })
                .catch(err => {
                    console.log(err.message)
                    setError("An error occured")
                })
        )
    }

    const showChunkSizeInput = (changedValues) => {
        const fieldName = Object.keys(changedValues)[0];

        if (fieldName === "tool") {
            const value = changedValues[fieldName];
            if (value == 1) {
                setToolId(value)
            } else {
                setToolId(null)
            }
        }
    }

    // extract file details 
    const normFile = (e) => {

        if (Array.isArray(e)) {
            return e[0];
        }
        return e && e.fileList[0];
    };


    return (
        <Drawer
            visible={visible}
            title="Create a Job"
            onClose={close}
            placement="right"
            closable={true}
            width={350}
        >
            <Form
                layout="vertical"
                onFinish={createJob}
                form={form}
                onValuesChange={showChunkSizeInput}
            >

                <Form.Item getValueProps={() => tool_id} label="Select Tool" name="tool" rules={[{ required: true, message: "Tool is required" }]}>
                    <Select
                        showSearch
                        placeholder="Select a tool"
                        defaultValue={tool_id}
                        optionFilterProp="children"
                        filterOption={(input, option) => option.key.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                            option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {
                            toolList.map(t => <Option title={t.name} value={t.id} key={t.id}>{t.id} - {t.name}</Option>)
                        }
                    </Select>
                </Form.Item>
                <Form.Item label="Job Name" name="name" rules={[{ required: true, message: "Job name is required" }, {
                    max: 100,
                    message: "Job name should not be more than 100 characters",
                }]}>
                    <Input />
                </Form.Item>
                <Form.Item label="Concurrency Factor" name="concurrency_factor"  >
                    <Input defaultValue="0" type="number" min="0" />
                </Form.Item>
                <Form.Item label="Chunk Size" name="chunk_size" defaultValue={"10"} style={toolid !== 1 ? { display: 'none' } : { display: 'block' }}>
                    <Input defaultValue="10" type="number" min="1" />
                </Form.Item>
                <Form.Item label="Description" name="description">
                    <Input.TextArea />
                </Form.Item>
                {
                    selectedTool && (
                        <Form.Item label="Sample file input">
                            <FileLink file_url={selectedTool.input_path} />
                        </Form.Item>
                    )
                }

                <Form.Item
                    label="Input file"
                    name="input_file"
                    getValueFromEvent={normFile}
                    extra="Please make sure the input file format matches the sample input file above. Download to ensure. Only required for LinkedIn scrapers."
                >
                    <Upload
                        name="input_file"
                        accept=".csv"
                        beforeUpload={() => {
                            return false
                        }}
                        multiple={false}
                    >
                        <Button icon={<UploadOutlined />} > Upload input file </Button>
                    </Upload>
                </Form.Item>
                <Form.Item label="Parameters" name="parameters">
                    <Form.List
                        name="parameters"
                        label="Parameters"
                        extra="Click on add field to record parameters"
                    >
                        {(fields, { add, remove }) => (
                            <>
                                {
                                    fields.map(({ key, name, fieldKey, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'param_key']}
                                                fieldKey={[fieldKey, 'param_key']}
                                                rules={[{ required: true, message: 'Param key is required' }]}
                                            >
                                                <Input placeholder="Parameter key" />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'param_value']}
                                                fieldKey={[fieldKey, 'param_value']}
                                                rules={[{ required: true, message: 'Param value is required' }]}
                                            >
                                                <Input placeholder="Parameter value" />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))
                                }

                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Add field
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form.Item>

                <Form.Item >
                    <CTAButton type="submit" loading={promiseInProgress}>Submit</CTAButton>
                </Form.Item>
                {
                    error && (
                        <Form.Item >
                            <p className={classes.error}>{error}</p>
                        </Form.Item>
                    )
                }
            </Form>
        </Drawer>
    )
}


AddNewJob.propTypes = {
    /** Control visibility of the drawer */
    visible: PropTypes.bool,
    /** Close the drawer */
    close: PropTypes.func,
    /** id of ool to use template to create the job from */
    tool_id: PropTypes.number

}

export default AddNewJob